// eslint-disable-next-line @typescript-eslint/no-use-before-define
import React, { useEffect, useState, useRef, useCallback } from 'react'
import { Box, Flex, Tooltip, InputGroup, InputRightElement, Spinner, Button, Text } from '@chakra-ui/core'
import { useSelector } from 'react-redux'
import isSolid from 'is-solid'
import { ReactComponent as MicrophoneIcon } from 'assets/microphone-icon.svg'
import { DropzoneWrapper } from 'components/DropzoneWrapper'
import { useDropzoneHandlers } from 'hooks/useDropzoneHandlers'

import colors from 'constants/colors'
import strings from 'constants/strings'
import { ReactComponent as CameraIcon } from 'assets/icon-capture.svg'
import { ReactComponent as IconSend } from 'assets/icon-send.svg'
import useLocaleText from 'components/useLocaleText'
import LocaleText from 'components/LocaleText'
import { AudioRecorder } from 'components/AudioRecorder'
import { PreviewFile } from 'config/types'
import { isRtlSelector } from 'redux/selectors/general'
import { CustomFieldWrapper, CustomText, CustomTextArea } from 'components/helperStyles'
import { InputTooltip } from './InputTooltip'
import { AudioRecordingButton } from './AudioRecordingButton'
import { isValidDomain } from 'utils'
import * as linkify from 'linkifyjs'
import { LinkPreview } from 'components/LinkPreview'
import HiddenFileInput from 'components/ui/Input/HiddenFileInput'
import { ImageEditorModal } from 'components/CommonComponents/ImageEditor/ImageEditorModal'
import { Paperclip } from 'lucide-react'

const isImageFile = (mimeType: string): boolean => mimeType.startsWith('image/')

interface IProps {
  postID: string
  handleCreateComment: (data: IHandleCreateComment) => Promise<void>
  sendCommentLoading: boolean
  isFocused: boolean
  replyCID?: string
  userTag?: string
  closeCallback?: () => void
  isReply?: boolean
  isChat?: boolean
  inputBgColor?: string
  initialCommentText?: string
}

interface IHandleCreateComment {
  comment: string
  images?: PreviewFile[]
  audios?: PreviewFile
  files?: PreviewFile[]
  videos?: PreviewFile[]
}

const CommentInput = ({
  postID,
  handleCreateComment,
  sendCommentLoading,
  userTag,
  isFocused = false,
  closeCallback,
  isReply,
  isChat = false,
  inputBgColor,
  initialCommentText,
}: IProps) => {
  // Refs
  const fileFormRef = useRef<HTMLFormElement | null>(null)
  const commentInputRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const fileAttachRef = useRef<HTMLInputElement>(null)

  // State
  const [isRecording, setIsRecording] = useState(false)
  const [commentText, setCommentText] = useState<string>(initialCommentText || '')
  const [isFormValid, setIsFormValid] = useState<boolean>(false)
  const [isFileUploading, setIsFileUploading] = useState(false)

  // Image editor state (replaces MediaModal)
  const [editorOpen, setEditorOpen] = useState(false)
  const [editorImages, setEditorImages] = useState<string[]>([])
  const blobToFileRef = useRef<Map<string, File>>(new Map())
  const [editorCommentText, setEditorCommentText] = useState('')

  const isRtl = useSelector(isRtlSelector)

  // Localization
  const t_write_a_comment = useLocaleText('write_a_comment')
  const attachFileLiteral = useLocaleText('post_upload_file_web_hover')
  const attachPhotoLiteral = useLocaleText('post_attach_photo_web_hover')
  const attachRecordLiteral = useLocaleText('post_attach_record_web_hover')

  const TEXTAREA_ID = postID + userTag
  const foundLinks = linkify.find(commentText)
  const validLinks = foundLinks.filter((link) => isValidDomain(link.href))

  const isBusy = sendCommentLoading || isFileUploading

  const checkForm = useCallback((): boolean => !!commentText.length, [commentText])

  useEffect(() => {
    setIsFormValid(checkForm())
  }, [checkForm])

  useEffect(() => {
    if (isFocused && commentInputRef.current) {
      commentInputRef.current.setSelectionRange(
        commentInputRef.current.value.length,
        commentInputRef.current.value.length
      )
      commentInputRef.current.click()
    }
  }, [isFocused])

  useEffect(() => {
    setCommentText(initialCommentText || '')
  }, [initialCommentText])

  // Cleanup blob URLs on unmount
  useEffect(() => {
    const ref = blobToFileRef.current
    return () => {
      ref.forEach((_, url) => URL.revokeObjectURL(url))
    }
  }, [])

  // ── Open editor ─────────────────────────────────────────────────────────
  const openEditor = () => {
    setEditorCommentText('')
    setEditorImages([])
    setEditorOpen(true)
  }

  // ── Pick from gallery (called from editor source picker) ────────────────
  const handlePickFromGallery = () => {
    fileInputRef.current?.click()
  }

  // ── Take photo (called from editor when camera captures a shot) ─────────
  const handleTakePhoto = useCallback((url: string) => {
    // The url is already an object URL pointing to a Blob — wrap it as a File
    fetch(url)
      .then((r) => r.blob())
      .then((blob) => {
        const file = new File([blob], `camera_${Date.now()}.jpg`, { type: 'image/jpeg' })
        blobToFileRef.current.set(url, file)
        setEditorImages((prev) => [...prev, url])
      })
  }, [])

  // ── User selected images via gallery picker ──────────────────────────────
  const handleImageFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files.length) return
    const selectedFiles = Array.from(e.target.files)
    const newBlobUrls: string[] = []
    selectedFiles.forEach((file) => {
      if (!isImageFile(file.type)) return
      const blobUrl = URL.createObjectURL(file)
      blobToFileRef.current.set(blobUrl, file)
      newBlobUrls.push(blobUrl)
    })
    if (newBlobUrls.length > 0) {
      setEditorImages((prev) => [...prev, ...newBlobUrls])
    }
    fileFormRef.current?.reset()
  }

  // ── Editor save ─────────────────────────────────────────────────────────
  const handleEditorSave = async (edits: Record<number, Blob>) => {
    if (editorImages.length === 0) {
      setEditorOpen(false)
      return
    }

    const finalFiles: PreviewFile[] = editorImages
      .map((blobUrl, i) => {
        const originalFile = blobToFileRef.current.get(blobUrl)
        const editBlob = edits[i]
        if (editBlob) {
          const name = originalFile?.name || `image_${i}.png`
          const editedFile = new File([editBlob], name, { type: 'image/png' })
          return { file: editedFile, previewUrl: URL.createObjectURL(editBlob) }
        }
        if (!originalFile) return null
        return { file: originalFile, previewUrl: blobUrl }
      })
      .filter(Boolean) as PreviewFile[]

    editorImages.forEach((url) => {
      URL.revokeObjectURL(url)
      blobToFileRef.current.delete(url)
    })

    setEditorImages([])
    setEditorOpen(false)

    if (finalFiles.length > 0) {
      await handleCreateComment({ comment: editorCommentText, images: finalFiles })
      setEditorCommentText('')
    }
  }

  // ── Editor cancel ───────────────────────────────────────────────────────
  const handleEditorCancel = () => {
    editorImages.forEach((url) => {
      URL.revokeObjectURL(url)
      blobToFileRef.current.delete(url)
    })
    setEditorImages([])
    setEditorOpen(false)
    setEditorCommentText('')
  }

  // ── Text comment submit ─────────────────────────────────────────────────
  const onSubmit = async (e?: any, audioLink?: PreviewFile) => {
    if (e) e.preventDefault()
    if (!checkForm() && !audioLink) return
    await handleCreateComment({ comment: commentText, audios: audioLink })
    setCommentText('')
    if (commentInputRef.current) commentInputRef.current.blur()
    if (closeCallback) closeCallback()
  }

  const onEnter = (e: React.KeyboardEvent) => {
    if (document.activeElement && document.activeElement.id === TEXTAREA_ID && e.key === 'Enter' && !e.shiftKey) {
      onSubmit()
      return true
    }
  }

  const onResizeTextareaHandler = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCommentText(e.target.value)
  }

  const onChangeHeightHandler = () => {
    if (!commentText && commentInputRef.current) {
      commentInputRef.current.style.height = '40px'
    }
  }

  const attachFileHandler = () => {
    fileAttachRef.current?.click()
  }

  const handleFileAttach = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files.length) return
    const files: PreviewFile[] = Array.from(e.target.files).map((file) => ({
      file,
      previewUrl: URL.createObjectURL(file),
    }))
    fileFormRef.current?.reset()
    setIsFileUploading(true)
    try {
      await handleCreateComment({ comment: commentText, files })
      setCommentText('')
    } finally {
      setIsFileUploading(false)
    }
  }

  // Audio
  const stopRecording = () => setIsRecording(false)
  const startRecording = () => setIsRecording(true)
  const sendRecord = async (audioLink: PreviewFile) => {
    if (audioLink) onSubmit(null, audioLink)
    setIsRecording(false)
  }

  const { onDrop, handlePaste } = useDropzoneHandlers({
    onUploadMedia: (uploadedFiles: File[]) => {
      if (!uploadedFiles.length) return Promise.resolve()
      const imgFiles = uploadedFiles.filter((f) => isImageFile(f.type))
      if (imgFiles.length > 0) {
        const newBlobUrls: string[] = []
        imgFiles.forEach((file) => {
          const blobUrl = URL.createObjectURL(file)
          blobToFileRef.current.set(blobUrl, file)
          newBlobUrls.push(blobUrl)
        })
        setEditorImages((prev) => [...prev, ...newBlobUrls])
        setEditorOpen(true)
      }
      return Promise.resolve()
    },
    accept: `${strings.POST_FILE_ACCEPT_EXTENSIONS},image/*,video/*`,
  })

  // ── Comment slot rendered inside ImageEditorModal ───────────────────────
  const commentSlot = (getPendingEdits: () => Record<number, Blob>) => (
    <Flex alignItems="center" style={{ columnGap: 8 }} w="100%">
      <CustomTextArea
        placeholder={t_write_a_comment}
        name="editor-comment"
        value={editorCommentText}
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setEditorCommentText(e.target.value)}
        maxRows={3}
        minRows={1}
        height="38px"
        style={{
          background: 'rgba(255,255,255,0.12)',
          color: '#F3F4F6',
          borderColor: 'rgba(255,255,255,0.2)',
          flex: 1,
        }}
      />
      <Button
        onClick={() => handleEditorSave(getPendingEdits())}
        bg={sendCommentLoading ? 'transparent' : colors.greenLight2}
        minWidth="auto"
        isDisabled={sendCommentLoading || editorImages.length === 0}
        width="38px"
        height="38px"
        cursor="pointer"
        justifyContent="center"
        alignItems="center"
        borderRadius="50%"
        p="0"
      >
        {sendCommentLoading ? <Spinner size="sm" color={colors.greyDark} /> : <IconSend width="22px" height="22px" />}
      </Button>
    </Flex>
  )

  return (
    <Box w="100%">
      {!!validLinks && validLinks.length > 0 && (
        <LinkPreview width="100%" url={validLinks[validLinks.length - 1].href} />
      )}
      <Flex mt="10px" mb={isChat ? '10px' : '5px'} alignItems="center">
        <Flex width={isReply ? '75%' : '100%'} minW="290px" flexDirection="column">
          <DropzoneWrapper onDrop={onDrop} onPaste={handlePaste}>
            <Flex width="100%" style={{ columnGap: '10px' }}>
              {!isRecording && (
                <CustomFieldWrapper flexWrap="wrap" flex={1}>
                  <InputGroup size="sm" flex={1}>
                    {isSolid(userTag) && (
                      <CustomText
                        position="absolute"
                        top={-18}
                        left={isRtl ? 'auto' : 2}
                        right={isRtl ? 2 : 'auto'}
                        fontSize="xs"
                      >
                        <LocaleText text="reply_to_title" />{' '}
                        <Text as="span" fontWeight={700} mb="10px">
                          {userTag}
                        </Text>
                      </CustomText>
                    )}
                    <CustomTextArea
                      placeholder={t_write_a_comment}
                      name="comment"
                      id={TEXTAREA_ID}
                      value={commentText}
                      disabled={isBusy || isRecording}
                      ref={commentInputRef}
                      onChange={onResizeTextareaHandler}
                      onKeyPress={onEnter}
                      onHeightChange={onChangeHeightHandler}
                      maxRows={5}
                      height="40px"
                      style={{
                        paddingRight: isRtl ? '8px' : '85px',
                        paddingLeft: isRtl ? '85px' : '8px',
                        background: inputBgColor ? inputBgColor : colors.greyMedium,
                      }}
                    />
                    <InputRightElement
                      width="5rem"
                      top="5px"
                      right={isRtl ? 'auto' : '5px'}
                      left={isRtl ? '5px' : 'auto'}
                    >
                      {/* Camera → opens ImageEditorModal source picker */}
                      <InputTooltip
                        tooltipText={attachPhotoLiteral}
                        isDisabled={isBusy}
                        onClick={(e) => {
                          e.preventDefault()
                          openEditor()
                        }}
                      >
                        <CameraIcon />
                      </InputTooltip>
                      {/* Paperclip → attach non-image files */}
                      <InputTooltip tooltipText={attachFileLiteral} isDisabled={isBusy} onClick={attachFileHandler}>
                        <Paperclip width={18} height={18} color="#A3A7AD" strokeWidth={1.5} />
                      </InputTooltip>
                    </InputRightElement>
                  </InputGroup>
                </CustomFieldWrapper>
              )}
              {isFormValid ? (
                <Button
                  onClick={onSubmit}
                  bg={isBusy ? 'transparent' : colors.greenLight2}
                  minWidth="auto"
                  isDisabled={isBusy}
                  width="40px"
                  height="40px"
                  cursor="pointer"
                  justifyContent="center"
                  alignItems="center"
                  borderRadius="50%"
                  p="0"
                  type="submit"
                >
                  {isBusy ? <Spinner size="sm" color={colors.greyDark} /> : <IconSend width="25px" height="25px" />}
                </Button>
              ) : isRecording ? (
                <AudioRecorder isRecording={isRecording} onSuccess={sendRecord} onStopRecording={stopRecording} />
              ) : (
                <Tooltip
                  aria-label={attachRecordLiteral}
                  label={attachRecordLiteral}
                  zIndex={100}
                  fontSize="14px"
                  p="10px"
                  placement="top"
                  closeOnClick
                >
                  <AudioRecordingButton onClick={startRecording} isLoading={isBusy}>
                    <MicrophoneIcon width="25px" height="25px" />
                  </AudioRecordingButton>
                </Tooltip>
              )}
            </Flex>
          </DropzoneWrapper>
        </Flex>
      </Flex>

      {/* File inputs */}
      <form ref={fileFormRef}>
        {/* Gallery picker */}
        <HiddenFileInput multiple={true} accept="image/*" ref={fileInputRef} onChange={handleImageFileInput} />
        {/* Non-image file attach */}
        <HiddenFileInput
          multiple={true}
          accept={strings.POST_FILE_ACCEPT_EXTENSIONS}
          ref={fileAttachRef}
          onChange={handleFileAttach}
        />
      </form>

      {/* ImageEditorModal replaces MediaModal for photo comments */}
      <ImageEditorModal
        mode="comment"
        isOpen={editorOpen}
        images={editorImages}
        hasLocalImages={editorImages.length > 0}
        onSave={handleEditorSave}
        onCancel={handleEditorCancel}
        onPickFromGallery={handlePickFromGallery}
        onTakePhoto={handleTakePhoto}
        commentSlot={commentSlot}
      />
    </Box>
  )
}

export default CommentInput
